export default defineNuxtRouteMiddleware(async (to, from) => {
    if (process.env.NODE_ENV === 'test' || process.env.VITEST === 'true') {
        return
    }

    // Skip middleware for test routes
    if (to.path.startsWith('/_tests/') || to.path.startsWith('/test-') || to.path.startsWith('/_e2e')) {
        return
    }

    // add uri and page type to global store
    const { pageType, uri, showHamburgerMenu } = storeToRefs(useGlobalStore())
    const { entry } = storeToRefs(useEntryStore())
    const micrositeStore = useMicrositeStore()

    // reset hamburger menu open state when page changes
    showHamburgerMenu.value = false

    // reset entry only on client-side navigations (avoid clearing during SSR/hydration)
    if (import.meta.client && from?.path) {
        entry.value = {}
    }

    if (to.path === '/') {
        uri.value = '__home__'
        pageType.value = 'home'
    } else {
        // Normalize uri (no leading or trailing slash)
        uri.value = to.path.replace(/^\/+|\/+$/g, '')
        pageType.value = 'page'
    }

    // We don't need to load entryData for the search page
    if (to.name === 'search') {
        return
    }

    // Article/Entry data is loaded on the page itself for news-feed & newsletter articles
    // so we can skip loading entry data for these routes in the middleware, and avoid potential hydration mismatches on SSR
    if (!to?.params?.article) {
        console.log('Loading entry data for route:', to.path)

        /*
         ** if the route could be a potential microsite article route, remove the microsite prefix
         ** it needs to be 3 segments long
         ** and the first segment needs to match a known microsite uri
         */
        const segments = to.path.split('/').filter(Boolean)
        let potentialArticleUri = uri.value

        if (segments.length >= 3 && ['news-feed', 'newsletters', 'news', 'events'].includes(segments[1])) {
            // Check if the first segment matches a known microsite
            const { micrositeSlug } = storeToRefs(useMicrositeStore())

            if (micrositeSlug.value) {
                // Remove the microsite prefix to get the article URI
                potentialArticleUri = segments.slice(1).join('/')

                /*
                 ** load the entry with the original uri as a fallback
                 ** this is needed as it's possible to for a client to create a normal page
                 ** on an expected article route, eg: /microsite/news/normal-page-slug
                 */
                await loadEntryForRoute(potentialArticleUri, uri.value, to.query ?? {})
            } else {
                await loadEntryForRoute(uri.value, undefined, to.query ?? {})
            }
        } else {
            console.log('Loading entry data for route:', uri.value)
            await loadEntryForRoute(uri.value, undefined, to.query ?? {})
        }
    }

    /*
     ** If this is a microsite route, ensure microsite data is loaded on the server
     ** before layouts render to avoid SSR/client hydration mismatch.
     */
    if (import.meta.server) {
        // Determine microsite slug safely for SSR.
        const { micrositeSlug } = storeToRefs(useMicrositeStore())
        /*
         ** we only need to load the microsite data seperately if it's not a standard microsite page
         ** these pages already return the microsite data in the query
         */
        if (micrositeSlug.value) {
            if (['ds_MicrositeHomepage_Entry', 'ds_MicrositePage_Entry'].includes(entry.value?.__typename)) {
                // if the entry data already contains the microsite data (eg: we're on a microsite page), we can initialize the microsite store directly
                await micrositeStore.initMicrosite(entry.value)
            } else {
                // if not, we need to fetch the microsite data to initialize the store
                await micrositeStore.loadMicrosite(micrositeSlug.value, { routeQuery: to.query ?? {} })
            }
        }
    }
})

// loads entry
const loadEntryForRoute = async (uri: string, alternateUri?: string, routeQuery?: Record<string, any>) => {
    const entryStore = useEntryStore()

    try {
        await entryStore.loadEntry(uri, { routeQuery })
    } catch (error) {
        // If the initial URI fails to load, attempt to load an alternate URI if provided
        if (alternateUri) {
            return await loadEntryForRoute(alternateUri, undefined, routeQuery)
        }

        // If both attempts fail, throw the original error
        throw createError(error)
    }
}
